import { flux } from '@/core/flux.module';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import type { CombinedColumnRuleInputParameters } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import type { ColumnRuleWithMetadata } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import type {
  AutoOpenColumnRule,
  ColumnDefinition,
  ControlPaneDisplay,
  EditorType,
  TableDefinitionColumnOptions,
  TableDefinitionTemplateOptions,
} from '@/tableDefinitionEditor/tableDefinition.types';

export function setTableId(editorType: EditorType, id: string) {
  flux.dispatch(`${editorType}_SET_TABLE_ID`, { tableId: id });
}

export function setShowConfigureSearch(editorType: EditorType, showConfigureSearch: boolean) {
  // when opening the item finder config, we currently close the item finder for the other editor in order to avoid
  // duplication of the item finder data
  const otherEditorType: EditorType = editorType === 'ITEM' ? 'SCALING' : 'ITEM';
  flux.dispatch(`${otherEditorType}_SET_SHOW_CONFIGURE_SEARCH`, { showConfigureSearch: false });
  resetItemFinder();
  flux.dispatch(`${editorType}_SET_SHOW_CONFIGURE_SEARCH`, { showConfigureSearch });
}

export function resetTableEditorStore(editorType: EditorType) {
  flux.dispatch(`${editorType}_RESET_TABLE_EDITOR_STORE`);
}

export function setColumnToEditId(editorType: EditorType, columnToEditId: string | undefined) {
  flux.dispatch(`${editorType}_SET_COLUMN_TO_EDIT_ID`, { columnToEditId });
}

export function setShowColumnProperties(editorType: EditorType, showColumnProperties: boolean) {
  flux.dispatch(`${editorType}_SET_SHOW_COLUMN_PROPERTIES`, { showColumnProperties });
}

export function setEnableTreeCreation(editorType: EditorType, enableTreeCreation: boolean) {
  flux.dispatch(`${editorType}_SET_ENABLE_TREE_CREATION`, { enableTreeCreation });
}

export function setSelectedColumnOption(
  editorType: EditorType,
  selectedColumnOption: TableDefinitionColumnOptions | undefined,
) {
  flux.dispatch(`${editorType}_SET_SELECTED_COLUMN_OPTION`, { selectedColumnOption });
}

export function setSelectedTemplateOption(
  editorType: EditorType,
  selectedTemplateOption: TableDefinitionTemplateOptions | undefined,
) {
  flux.dispatch(`${editorType}_SET_SELECTED_TEMPLATE_OPTION`, { selectedTemplateOption });
}

export function setControlPane(editorType: EditorType, controlPane: ControlPaneDisplay) {
  flux.dispatch(`${editorType}_SET_CONTROL_PANE`, { controlPane });
}

export function setShortcutSourceColumn(editorType: EditorType, shortcutSourceColumn: ColumnDefinition | undefined) {
  flux.dispatch(`${editorType}_SET_SHORTCUT_SOURCE_COLUMN`, { shortcutSourceColumn });
}

export function setInitialColumnRule(editorType: EditorType, initialColumnRule: ColumnRuleWithMetadata | undefined) {
  flux.dispatch(`${editorType}_SET_INITIAL_COLUMN_RULE`, { initialColumnRule });
}

export function setInitialRuleParameterOverrides(
  editorType: EditorType,
  initialRuleParameterOverrides: Partial<CombinedColumnRuleInputParameters> | undefined,
) {
  flux.dispatch(`${editorType}_SET_INITIAL_RULE_PARAMETER_OVERRIDES`, { initialRuleParameterOverrides });
}

export function setAutoOpenColumnRule(editorType: EditorType, autoOpenColumnRule: AutoOpenColumnRule | undefined) {
  flux.dispatch(`${editorType}_SET_AUTO_OPEN_COLUMN_RULE`, { autoOpenColumnRule });
}

export function setShowStoredTableSearchWarning(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_SET_SHOW_STORED_TABLE_SEARCH_WARNING`, { show });
}

export function setShowChildLimitExceededWarning(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_SET_SHOW_CHILD_LIMIT_EXCEEDED_WARNING`, { show });
}

export function setHierarchyCount(editorType: EditorType, hierarchyCount: number) {
  flux.dispatch(`${editorType}_SET_HIERARCHY_COUNT`, { hierarchyCount });
}
